<template>
  <div class="topic in-list d-flex flex-sm-column flex-md-row" :class="topicClasses">
    <div class="flex-md-grow-1 truncate-overflow-in-children" @click="openTopic()">
      <div class="topic-title truncate-overflow">
        <b v-dompurify-html="topic.title"></b>
      </div>
      <div v-if="descriptionOverflows" class="float-end">
        <button
          class="btn btn-link btn-sm expand_btn"
          @click="toggleDescriptionDivExpansion"
          v-tooltip.bottom="'Beschreibung aus-/einklappen'"
        >
          <font-awesome-icon v-if="descriptionDivExpanded" icon="circle-chevron-up" />
          <font-awesome-icon v-else icon="circle-chevron-down" />
        </button>
      </div>
      <div class="topic-description" :class="{ 'truncate-overflow': !descriptionDivExpanded }" ref="descriptionDiv">
        <small v-dompurify-html="plainTextDescription"></small>
        <br />
      </div>
      <div class="topic-extras truncate-overflow">
        <small>Themen: {{ topic.thoughtcount }}, Beiträge: {{ topic.commentcount }}</small>
      </div>
    </div>
    <div class="restricted_access_controls" v-if="topic.restrict_public_access == 1">
      <template v-if="topic.restricted_access == 'allowed'">
        <font-awesome-icon icon="unlock-alt" />
      </template>
      <template v-else>
        <div class="float-end">
          <font-awesome-icon icon="lock" />
        </div>
        <div v-if="topic.restricted_access == 'requested'">Zugriffsanfrage gestellt</div>
        <div v-else>
          <button class="btn btn-sm btn-default" @click="openRequestAccessDialog">Zugriff anfragen</button>
          <OverlayPanel
            ref="requestAccessOP"
            :showCloseIcon="true"
            :dismissable="true"
            appendTo="body"
            class="wdw-widgets"
          >
            <template v-if="isLoggedIn">
              <p>Zugriff auf diesen Bereich ist eingeschränkt. Möchten Sie Zugriff anfragen?</p>
              <button class="btn btn-primary" @click="requestAccessSubmit(true)">Zugriff anfragen</button>
              <button class="btn btn-default" @click="requestAccessSubmit(false)">Abbrechen</button>
            </template>
            <template v-else>
              <p>Um Zugriff auf den Bereich zu erhalten, müssen Sie sich zunächst einloggen!</p>
              <Login :domainid="domainid" />
            </template>
          </OverlayPanel>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import { ref, computed } from 'vue';

// stores
import { userStore } from '@/store/user-store';
import { domainStore } from '@/store/domain-store';

// components
import OverlayPanel from 'primevue/overlaypanel';
import Login from '@/components/user/Login.vue';

export default {
  name: 'forum-topic-in-list',
  props: {
    topic: {
      type: Object,
      required: true,
    },
  },
  emit: ['show-topic', 'request-access'],

  setup(props, context) {
    const requestAccessOP = ref();
    const descriptionDiv = ref();
    const descriptionDivExpanded = ref(false);

    //
    //-----------------COMPUTED------------------
    //
    const isLoggedIn = computed(() => {
      return userStore.isLoggedIn();
    });
    const domainid = computed(() => {
      return domainStore.getState().domainid;
    });
    const plainTextDescription = computed(() => {
      const doc = new DOMParser().parseFromString(props.topic.description, 'text/html');
      return doc.body.textContent || '';
    });
    const descriptionOverflows = computed(() => {
      if (descriptionDiv.value) {
        const tag = document.createElement('div');
        tag.style.position = 'absolute';
        tag.style.left = '-99in';
        tag.style.whiteSpace = 'nowrap';
        tag.innerHTML = plainTextDescription.value;

        document.body.appendChild(tag);
        const textWidth = tag.clientWidth;
        document.body.removeChild(tag);
        return textWidth >= descriptionDiv.value.offsetWidth;
      }
      return false;
    });
    const canAccessTopic = computed(() => {
      return props.topic.restrict_public_access == 0 || props.topic.restricted_access == 'allowed';
    });
    const topicClasses = computed(() => {
      return {
        ['topic-' + props.topic.id]: true,
        accessible: canAccessTopic.value,
      };
    });

    //
    //-----------------METHODS------------------
    //

    function openTopic() {
      if (canAccessTopic.value) {
        context.emit('show-topic', { topicid: props.topic.id });
      }
    }

    function openRequestAccessDialog(event) {
      requestAccessOP.value.show(event);
    }

    function requestAccessSubmit(value) {
      if (value) {
        context.emit('request-access', props.topic.id);
      }
      requestAccessOP.value.hide();
    }

    function toggleDescriptionDivExpansion(event) {
      event.stopPropagation();
      // console.log("toggle");
      // console.log(event);
      descriptionDivExpanded.value = !descriptionDivExpanded.value;
    }

    return {
      // REFS
      requestAccessOP,
      descriptionDiv,
      descriptionDivExpanded,

      // COMPUTED
      domainid,
      isLoggedIn,
      plainTextDescription,
      topicClasses,
      descriptionOverflows,

      // METHODS
      openTopic,
      openRequestAccessDialog,
      requestAccessSubmit,
      toggleDescriptionDivExpansion,
    };
  },

  components: {
    OverlayPanel,
    Login,
  },
};
</script>

<style scoped lang="scss">
.topic.in-list {
  padding: 10px;
  border-top: solid 1px #eee;
  &:hover {
    cursor: not-allowed;
  }

  &.accessible:hover {
    cursor: pointer;
    background-color: rgb(250, 250, 250);
  }
}
.restricted_access_controls,
.topic-extras {
  color: #888;
}
.restricted_access_controls {
  margin-left: 0.5em;
  button {
    margin-top: 0.2em;
  }
}
.btn.expand_btn {
  margin-left: 0.5em;
  line-height: 1em;
  font-size: 1.2rem;
  margin-top: -0.2rem;
}
</style>
